import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ApiError, apiFetch } from '../../lib/api';
import { DOMAIN_FIELDS, type ImportJobDetail, type ImportJobRowItem } from './types';
import { SkeletonBlock, SkeletonRows } from '../skeleton';

function ErrorLine({ error }: { readonly error: unknown }): React.JSX.Element | null {
  const { t } = useTranslation();
  if (!(error instanceof ApiError)) return null;
  return (
    <p className="field-error">
      {t(`errors:${error.code}`, { defaultValue: error.code, ...error.params })}
    </p>
  );
}

/**
 * 导入向导详情页（S17 步骤③–⑦）：③字段映射（MAPPED 前）→ ④校验（触发，
 * 请求内同步完成，D-30 第 11 条）→ ⑤预览/错误报告 + ⑥确认（VALIDATED
 * 后合并一屏）→ ⑦执行。COMPLETED/EXECUTION_FAILED/CANCELLED 为只读小结。
 */
export function ImportJobDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const jobId = id!;
  const { t } = useTranslation();
  const location = useLocation() as { state?: { headers?: string[]; suggestedMapping?: Record<string, string> } };

  const { data: job, isPending } = useQuery({
    queryKey: ['import-jobs', 'detail', jobId],
    queryFn: () => apiFetch<ImportJobDetail>(`/import-jobs/${jobId}`),
  });
  const { data: rowsData } = useQuery({
    queryKey: ['import-jobs', 'rows', jobId],
    // 行数据在上传那一刻就已经写好（createJob() 同事务插入 import_job_row），
    // 只要 job 存在就该能取——UPLOADED/MAPPED 阶段的字段映射步骤恰恰需要
    // 从第一行的 raw_data 键名反推文件表头，此前误把这一状态排除在外，
    // 导致直接刷新/直接打开链接进入映射步骤时表头是空的（找不到候选列）。
    queryFn: () => apiFetch<{ items: ImportJobRowItem[] }>(`/import-jobs/${jobId}/rows`),
    enabled: !!job,
  });

  if (isPending || !job)
    return (
      <div>
        <SkeletonBlock height={48} />
        <SkeletonRows rows={6} />
      </div>
    );

  const headers =
    location.state?.headers ??
    (rowsData?.items[0] ? Object.keys(rowsData.items[0].rawData) : []);

  return (
    <div style={{ maxWidth: '960px' }}>
      <Link to="/import-jobs" className="field-hint">
        {t('importExport.backToImports')}
      </Link>
      <div className="row row--between" style={{ marginTop: 'var(--space-2)' }}>
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{job.docNo}</h1>
        <span className="badge">{t(`importExport.status${job.status}`)}</span>
      </div>
      <p className="field-hint">
        {t(`importExport.domain${job.domain}`)} · {job.originalFilename} · {t('importExport.rowsSummary', { total: job.rowCount, valid: job.validCount, error: job.errorCount })}
      </p>

      {(job.status === 'UPLOADED' || job.status === 'MAPPED') && (
        <MappingStep
          jobId={jobId}
          domain={job.domain}
          headers={headers}
          initialMapping={job.fieldMapping ?? location.state?.suggestedMapping ?? {}}
        />
      )}
      {job.status === 'VALIDATED' && <ReviewStep jobId={jobId} job={job} rows={rowsData?.items ?? []} />}
      {job.status === 'VALIDATING' || job.status === 'EXECUTING' ? <p>{t('app.loading')}</p> : null}
      {(job.status === 'COMPLETED' || job.status === 'EXECUTION_FAILED' || job.status === 'VALIDATION_FAILED') && (
        <Summary job={job} rows={rowsData?.items ?? []} />
      )}
      {job.status === 'CANCELLED' && <p className="field-hint">{t('importExport.cancelledNotice')}</p>}
    </div>
  );
}

// =============================================================================
// ③ 字段映射
// =============================================================================
function MappingStep({
  jobId,
  domain,
  headers,
  initialMapping,
}: {
  readonly jobId: string;
  readonly domain: keyof typeof DOMAIN_FIELDS;
  readonly headers: readonly string[];
  readonly initialMapping: Record<string, string>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [mapping, setMapping] = useState<Record<string, string>>(initialMapping);
  const fields = DOMAIN_FIELDS[domain];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['import-jobs', 'detail', jobId] });
    void queryClient.invalidateQueries({ queryKey: ['import-jobs', 'rows', jobId] });
  };

  const saveMappingMutation = useMutation({
    mutationFn: () => apiFetch(`/import-jobs/${jobId}/mapping`, { method: 'POST', body: { mapping } }),
    onSuccess: invalidate,
  });
  const validateMutation = useMutation({
    mutationFn: async () => {
      await saveMappingMutation.mutateAsync();
      return apiFetch(`/import-jobs/${jobId}/validate`, { method: 'POST' });
    },
    onSuccess: invalidate,
  });
  const cancelMutation = useMutation({
    mutationFn: () => apiFetch(`/import-jobs/${jobId}/cancel`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const missingRequired = fields.filter((f) => f.required && !mapping[f.key]);

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <h2>{t('importExport.mappingTitle')}</h2>
      <p className="field-hint">{t('importExport.mappingHint')}</p>
      <div className="table" style={{ display: 'block' }}>
        {fields.map((field) => (
          <div key={field.key} className="row" style={{ gap: 'var(--space-3)', padding: 'var(--space-2) 0' }}>
            <span style={{ width: '220px' }}>
              {t(`importExport.field.${domain}.${field.key}`)}
              {field.required && <span style={{ color: 'var(--color-danger)' }}> *</span>}
            </span>
            <select
              style={{ flex: 1 }}
              value={mapping[field.key] ?? ''}
              onChange={(e) => setMapping((m) => ({ ...m, [field.key]: e.target.value }))}
            >
              <option value="">{t('importExport.unmapped')}</option>
              {headers.map((h) => (
                <option key={h} value={h}>
                  {h}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <ErrorLine error={saveMappingMutation.error} />
      <ErrorLine error={validateMutation.error} />
      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-4)' }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={missingRequired.length > 0 || validateMutation.isPending}
          onClick={() => validateMutation.mutate()}
        >
          {validateMutation.isPending ? t('importExport.validating') : t('importExport.validateButton')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => cancelMutation.mutate()}>
          {t('importExport.cancelImport')}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// ⑤ 预览/错误报告 + ⑥ 确认 + ⑦ 执行
// =============================================================================
function ReviewStep({
  jobId,
  job,
  rows,
}: {
  readonly jobId: string;
  readonly job: ImportJobDetail;
  readonly rows: readonly ImportJobRowItem[];
}): React.JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [acknowledgedErrors, setAcknowledgedErrors] = useState(false);
  const errorRows = useMemo(() => rows.filter((r) => r.status === 'ERROR'), [rows]);

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['import-jobs', 'detail', jobId] });
    void queryClient.invalidateQueries({ queryKey: ['import-jobs', 'rows', jobId] });
  };
  const executeMutation = useMutation({
    mutationFn: () => apiFetch<{ importedCount: number; skippedCount: number }>(`/import-jobs/${jobId}/execute`, { method: 'POST' }),
    onSuccess: invalidate,
  });
  const cancelMutation = useMutation({
    mutationFn: () => apiFetch(`/import-jobs/${jobId}/cancel`, { method: 'POST' }),
    onSuccess: invalidate,
  });

  const downloadErrorReport = (): void => {
    const header = ['row', 'field', 'error'].join(',');
    const lines = errorRows.map((r) =>
      (r.errors ?? [])
        .map((e) => [r.rowNumber, e.field ?? '', t(`errors:${e.code}`, { defaultValue: e.code, ...e.params })])
        .map((cols) => cols.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
        .join('\n'),
    );
    const csv = [header, ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${job.docNo}-errors.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const needsAck = errorRows.length > 0 && !acknowledgedErrors;

  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      <h2>{t('importExport.reviewTitle')}</h2>
      <p>
        {t('importExport.reviewSummary', { valid: job.validCount, error: job.errorCount, total: job.rowCount })}
      </p>

      {errorRows.length > 0 && (
        <>
          <div className="row row--between">
            <h3 style={{ color: 'var(--color-danger)' }}>{t('importExport.errorRowsTitle')}</h3>
            <button type="button" className="btn btn--ghost btn--sm" onClick={downloadErrorReport}>
              {t('importExport.downloadErrorReport')}
            </button>
          </div>
          <div className="table" style={{ display: 'block', maxHeight: '320px', overflowY: 'auto' }}>
            {errorRows.slice(0, 200).map((row) => (
              <div key={row.id} style={{ padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-border)' }}>
                <strong>{t('importExport.rowLabel', { row: row.rowNumber })}</strong>
                <div className="field-hint">
                  {(row.errors ?? [])
                    .map((e) => t(`errors:${e.code}`, { defaultValue: e.code, ...e.params }))
                    .join('; ')}
                </div>
              </div>
            ))}
          </div>
          <label className="row" style={{ gap: 'var(--space-2)', margin: 'var(--space-3) 0' }}>
            <input
              type="checkbox"
              checked={acknowledgedErrors}
              onChange={(e) => setAcknowledgedErrors(e.target.checked)}
            />
            <span>{t('importExport.acknowledgeSkipErrors', { count: errorRows.length })}</span>
          </label>
        </>
      )}

      <p>{t('importExport.confirmSummary', { count: job.validCount })}</p>

      <ErrorLine error={executeMutation.error} />
      <div className="row" style={{ gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={needsAck || job.validCount === 0 || executeMutation.isPending}
          onClick={() => executeMutation.mutate()}
        >
          {executeMutation.isPending ? t('importExport.executing') : t('importExport.executeButton')}
        </button>
        <button type="button" className="btn btn--ghost" onClick={() => cancelMutation.mutate()}>
          {t('importExport.cancelImport')}
        </button>
      </div>
    </div>
  );
}

// =============================================================================
// 只读小结
// =============================================================================
function Summary({
  job,
  rows,
}: {
  readonly job: ImportJobDetail;
  readonly rows: readonly ImportJobRowItem[];
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div style={{ marginTop: 'var(--space-3)' }}>
      {job.failureReason && <p className="field-error">{job.failureReason}</p>}
      {job.status === 'COMPLETED' && (
        <p>
          {t('importExport.completedSummary', { imported: job.importedCount, skipped: job.skippedCount })}
        </p>
      )}
      <div className="table" style={{ display: 'block' }}>
        {rows.slice(0, 500).map((row) => (
          <div key={row.id} className="tree__row" style={{ padding: 'var(--space-2) 0' }}>
            <span style={{ width: '80px' }}>{row.rowNumber}</span>
            <span style={{ width: '160px' }}>{t(`importExport.rowStatus${row.status}`)}</span>
            <span className="field-hint">
              {row.errors ? row.errors.map((e) => t(`errors:${e.code}`, { defaultValue: e.code, ...e.params })).join('; ') : ''}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
